import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ChevronLeft, CheckCircle2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { RouteTransitionLogo } from '../components/RouteTransitionLogo';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ForgotPasswordPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error('Please enter your email');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, {
        email,
        origin: window.location.origin,
      });
      setSent(true);
    } catch {
      // Endpoint is intentionally generic; only network errors reach here.
      setSent(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5, ease: [0.25, 0.1, 0.25, 1] }}
      className="app-shell premium-page"
      style={{
        minHeight: '100vh',
        minHeight: '100dvh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        backgroundColor: 'var(--app-bg)',
        position: 'relative',
        paddingLeft: 24, paddingRight: 24, paddingBottom: 40,
      }}
    >
      <button
        onClick={() => navigate('/login')}
        aria-label="Go back"
        data-testid="forgot-back-button"
        style={{ position: 'absolute', top: 'calc(env(safe-area-inset-top, 0px) + 20px)', left: 20, background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}
      >
        <ChevronLeft size={26} color="var(--text)" strokeWidth={1.75} />
      </button>

      <div className="auth-fade-up" style={{ animationDelay: '0ms' }}>
        <RouteTransitionLogo width={120} height={108} dataTestId="forgot-shared-logo" style={{ marginTop: 56 }} />
      </div>

      {!sent ? (
        <>
          <h1
            className="auth-fade-up"
            style={{ fontFamily: "'Amarante', serif", fontSize: 30, fontWeight: 400, color: 'var(--text)', textAlign: 'center', marginTop: 14, marginBottom: 8, animationDelay: '100ms' }}
          >
            Reset Password
          </h1>
          <p
            className="auth-fade-up"
            style={{ fontFamily: "'Outfit', sans-serif", fontSize: 14, color: 'var(--text-2)', textAlign: 'center', maxWidth: 320, lineHeight: 1.5, marginBottom: 28, animationDelay: '120ms' }}
          >
            Enter the email associated with your account and we&apos;ll send you a link to reset your password.
          </p>

          <form
            id="forgot-form"
            onSubmit={handleSubmit}
            className="premium-card auth-fade-up"
            style={{ width: '100%', maxWidth: 360, padding: '28px 24px', animationDelay: '200ms' }}
          >
            <label htmlFor="forgot-email" className="auth-label">E-mail</label>
            <input
              id="forgot-email"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@email.com"
              data-testid="forgot-password-email-input"
              className="auth-input"
            />
          </form>

          <motion.button
            type="submit"
            form="forgot-form"
            disabled={loading}
            whileHover={loading ? {} : { scale: 1.01 }}
            whileTap={loading ? {} : { scale: 0.99 }}
            className="auth-primary-btn auth-fade-up"
            data-testid="forgot-password-submit-button"
            style={{ maxWidth: 360, marginTop: 24, animationDelay: '300ms' }}
          >
            {loading ? 'Sending…' : 'Send Reset Link'}
          </motion.button>
        </>
      ) : (
        <div className="auth-fade-up" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginTop: 24, maxWidth: 360 }}>
          <CheckCircle2 size={56} color="#E52445" strokeWidth={1.5} style={{ marginBottom: 16 }} data-testid="forgot-success-icon" />
          <h1 style={{ fontFamily: "'Amarante', serif", fontSize: 28, fontWeight: 400, color: 'var(--text)', textAlign: 'center', marginBottom: 8 }}>
            Check your email
          </h1>
          <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: 14, color: 'var(--text-2)', textAlign: 'center', lineHeight: 1.5, marginBottom: 28 }}>
            If an account exists for <strong style={{ color: 'var(--text)' }}>{email}</strong>, we&apos;ve sent a link to reset your password.
          </p>
        </div>
      )}

      <button
        type="button"
        onClick={() => navigate('/login')}
        data-testid="back-to-login-link"
        className="auth-link auth-fade-up"
        style={{ marginTop: 24, display: 'flex', alignItems: 'center', gap: 6, animationDelay: '360ms' }}
      >
        <ChevronLeft size={16} /> Back to Login
      </button>
    </motion.div>
  );
}
